/**
 * Enhanced Error Handler - Context-aware error messages with recovery guidance
 * Provides actionable error messages and suggested recovery steps
 */

import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Typography,
  Collapse,
  IconButton,
  Stack,
  Chip,
  Paper,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  BugReport as BugIcon,
  Help as HelpIcon,
  AutoAwesome as SmartIcon,
  NavigateNext as ArrowIcon,
} from '@mui/icons-material';

export interface ErrorInfo {
  code?: string;
  message: string;
  details?: string;
  context?: 'project' | 'scenario' | 'dataset' | 'data-import' | 'optimization' | 'api' | 'validation';
  severity?: 'error' | 'warning' | 'info';
  recoverable?: boolean;
  suggestions?: RecoverySuggestion[];
}

export interface RecoverySuggestion {
  action: string;
  label: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  callback?: () => void;
}

interface EnhancedErrorHandlerProps {
  error: ErrorInfo;
  onRetry?: () => void;
  onDismiss?: () => void;
  compact?: boolean;
  showDetails?: boolean;
}

/**
 * Analyzes error and generates contextual recovery suggestions
 */
const generateRecoverySuggestions = (error: ErrorInfo): RecoverySuggestion[] => {
  const suggestions: RecoverySuggestion[] = [];

  // Common network/API errors
  if (error.message.toLowerCase().includes('network') || 
      error.message.toLowerCase().includes('failed to fetch') ||
      error.code?.includes('NETWORK')) {
    suggestions.push({
      action: 'retry',
      label: 'Retry Request',
      description: 'Try the operation again - network issues are often temporary',
      priority: 'high'
    });
    suggestions.push({
      action: 'check-connection',
      label: 'Check Connection',
      description: 'Verify your internet connection is stable',
      priority: 'medium'
    });
  }

  // Authentication errors
  if (error.message.toLowerCase().includes('unauthorized') || 
      error.message.toLowerCase().includes('authentication') ||
      error.code?.includes('AUTH')) {
    suggestions.push({
      action: 'login',
      label: 'Sign In Again',
      description: 'Your session may have expired - please sign in again',
      priority: 'high'
    });
  }

  // Permission errors
  if (error.message.toLowerCase().includes('permission') || 
      error.message.toLowerCase().includes('forbidden') ||
      error.code?.includes('PERMISSION')) {
    suggestions.push({
      action: 'check-permissions',
      label: 'Check Permissions',
      description: 'You may not have permission to perform this action',
      priority: 'high'
    });
    suggestions.push({
      action: 'contact-admin',
      label: 'Contact Administrator',
      description: 'Request additional permissions from your system administrator',
      priority: 'medium'
    });
  }

  // Validation errors
  if (error.message.toLowerCase().includes('validation') || 
      error.message.toLowerCase().includes('invalid') ||
      error.code?.includes('VALIDATION')) {
    suggestions.push({
      action: 'check-data',
      label: 'Verify Data Format',
      description: 'Check that your data follows the required format and constraints',
      priority: 'high'
    });
    suggestions.push({
      action: 'see-examples',
      label: 'View Examples',
      description: 'Look at example data formats and templates',
      priority: 'medium'
    });
  }

  // Dataset-specific errors
  if (error.context === 'dataset' || error.message.toLowerCase().includes('dataset')) {
    suggestions.push({
      action: 'select-dataset',
      label: 'Select Different Dataset',
      description: 'Try using a different dataset or create a new one',
      priority: 'high'
    });
    suggestions.push({
      action: 'create-dataset',
      label: 'Create New Dataset',
      description: 'Start fresh with a new dataset',
      priority: 'medium'
    });
  }

  // Data import errors
  if (error.context === 'data-import' || error.message.toLowerCase().includes('import')) {
    suggestions.push({
      action: 'check-file-format',
      label: 'Check File Format',
      description: 'Ensure your CSV file uses the correct format and encoding',
      priority: 'high'
    });
    suggestions.push({
      action: 'download-template',
      label: 'Download Template',
      description: 'Use the provided CSV template to format your data correctly',
      priority: 'medium'
    });
    suggestions.push({
      action: 'validate-data',
      label: 'Validate Data',
      description: 'Check for missing required fields or invalid values',
      priority: 'medium'
    });
  }

  // Optimization errors
  if (error.context === 'optimization' || error.message.toLowerCase().includes('vroom')) {
    suggestions.push({
      action: 'check-vroom-data',
      label: 'Verify VROOM Requirements',
      description: 'Ensure all locations have coordinates and vehicles have capacity',
      priority: 'high'
    });
    suggestions.push({
      action: 'reduce-data-size',
      label: 'Reduce Data Size',
      description: 'Try optimizing with fewer locations or vehicles',
      priority: 'medium'
    });
  }

  // If no specific suggestions, add generic ones
  if (suggestions.length === 0) {
    suggestions.push({
      action: 'refresh',
      label: 'Refresh Page',
      description: 'Reload the page to reset the application state',
      priority: 'medium'
    });
    suggestions.push({
      action: 'try-later',
      label: 'Try Again Later',
      description: 'The issue might resolve itself after some time',
      priority: 'low'
    });
  }

  return suggestions;
};

/**
 * Formats error message for better readability
 */
const formatErrorMessage = (message: string): string => {
  // Remove technical prefixes
  let formatted = message.replace(/^Error:\s*/, '');
  formatted = formatted.replace(/^Failed to\s*/, '');
  
  // Capitalize first letter
  formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
  
  // Add period if missing
  if (!formatted.endsWith('.') && !formatted.endsWith('!') && !formatted.endsWith('?')) {
    formatted += '.';
  }
  
  return formatted;
};

export const EnhancedErrorHandler: React.FC<EnhancedErrorHandlerProps> = ({
  error,
  onRetry,
  onDismiss,
  compact = false,
  showDetails = false
}) => {
  const [expanded, setExpanded] = React.useState(showDetails);
  const [showSuggestions, setShowSuggestions] = React.useState(false);

  const suggestions = error.suggestions || generateRecoverySuggestions(error);
  const formattedMessage = formatErrorMessage(error.message);
  const severity = error.severity || 'error';

  const handleSuggestionClick = (suggestion: RecoverySuggestion) => {
    if (suggestion.callback) {
      suggestion.callback();
    } else {
      // Default actions
      switch (suggestion.action) {
        case 'retry':
          onRetry?.();
          break;
        case 'refresh':
          window.location.reload();
          break;
        case 'login':
          // Would trigger login flow
          console.log('Redirect to login');
          break;
        default:
          console.log('Suggestion action:', suggestion.action);
      }
    }
  };

  if (compact) {
    return (
      <Alert
        severity={severity}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {onRetry && (
              <Button size="small" onClick={onRetry}>
                Retry
              </Button>
            )}
            {onDismiss && (
              <Button size="small" onClick={onDismiss}>
                Dismiss
              </Button>
            )}
          </Box>
        }
      >
        {formattedMessage}
      </Alert>
    );
  }

  return (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Alert
        severity={severity}
        action={
          <Stack direction="row" spacing={1}>
            {suggestions.length > 0 && (
              <Button
                size="small"
                startIcon={<SmartIcon />}
                onClick={() => setShowSuggestions(!showSuggestions)}
              >
                Solutions
              </Button>
            )}
            {onRetry && (
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={onRetry}
              >
                Retry
              </Button>
            )}
            {error.details && (
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Stack>
        }
        sx={{ '& .MuiAlert-message': { width: '100%' } }}
      >
        <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {error.context && (
            <Chip
              label={error.context.replace('-', ' ')}
              size="small"
              variant="outlined"
              sx={{ textTransform: 'capitalize' }}
            />
          )}
          Error Occurred
          {error.code && (
            <Typography variant="caption" color="text.secondary">
              ({error.code})
            </Typography>
          )}
        </AlertTitle>
        
        <Typography variant="body2">
          {formattedMessage}
        </Typography>

        {/* Error Details */}
        {error.details && (
          <Collapse in={expanded}>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Technical Details:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {error.details}
              </Typography>
            </Box>
          </Collapse>
        )}
      </Alert>

      {/* Recovery Suggestions */}
      <Collapse in={showSuggestions}>
        <Box sx={{ p: 2, bgcolor: 'background.default' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmartIcon color="primary" fontSize="small" />
            Suggested Solutions
          </Typography>
          
          <Stack spacing={1}>
            {suggestions
              .sort((a, b) => {
                const priorityOrder = { high: 0, medium: 1, low: 2 };
                return priorityOrder[a.priority] - priorityOrder[b.priority];
              })
              .map((suggestion, index) => (
                <Paper
                  key={index}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      transform: 'translateX(4px)',
                    }
                  }}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Chip
                      label={suggestion.priority}
                      size="small"
                      color={suggestion.priority === 'high' ? 'error' : 
                             suggestion.priority === 'medium' ? 'warning' : 'default'}
                      variant="outlined"
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" fontWeight="600">
                        {suggestion.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {suggestion.description}
                      </Typography>
                    </Box>
                    <ArrowIcon color="action" fontSize="small" />
                  </Box>
                </Paper>
              ))}
          </Stack>

          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Still need help? Check our documentation or contact support.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" startIcon={<HelpIcon />}>
                Help
              </Button>
              <Button size="small" startIcon={<BugIcon />}>
                Report Issue
              </Button>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default EnhancedErrorHandler;